import { useMutation, useQuery } from '@tanstack/react-query';

import { useMeContext } from '@/_providers';
import type { ApiError } from '@/_services';
import { apiClient, queryClient } from '@/_services';

import type { PurchasedPracticeExamSchema } from '../../@shared';

import type { PracticeExamUpdateSchema, AttemptSchema } from './schemas';

export const useGetUserAssessmentDetails = (userAssessmentId: number | null) => {
  const { data } = useMeContext();
  const { id } = data || {};

  return useQuery<
    { status: string; data: { assessment: PurchasedPracticeExamSchema }[] },
    unknown,
    PurchasedPracticeExamSchema[]
  >({
    queryKey: ['PRACTICE_EXAMS_DETAILS', userAssessmentId, id],
    enabled: !!userAssessmentId && !!id,
    queryFn: async () =>
      apiClient.post({
        endpoint: `/user-assessment/details/`,
        payload: { user_id: id, assessment_id: userAssessmentId },
      }),
  });
};

export const useGetPracticeExamAttempts = (userAssessmentId: number | null) => {
  return useQuery<{ data: PurchasedPracticeExamSchema[] }, unknown, PurchasedPracticeExamSchema[]>({
    queryKey: ['PRACTICE_EXAMS_CHILDREN', userAssessmentId],
    enabled: !!userAssessmentId,
    queryFn: async () =>
      apiClient.request({
        endpoint: `/prep-assessment/${userAssessmentId}/childs/`,
      }),
    select: (data) => data.data,
  });
};

export const removePracticeExamChildren = (userAssessmentId: number | null) => {
  return queryClient.removeQueries({ queryKey: ['PRACTICE_EXAMS_CHILDREN', userAssessmentId] });
};

export const useGetPrepAssessmentDetails = (userAssessmentId: number | null) => {
  return useQuery<AttemptSchema, ApiError, AttemptSchema>({
    queryKey: ['RESET_PRACTICE_EXAM', 'PRACTICE_EXAMS_DETAILS', userAssessmentId],
    enabled: !!userAssessmentId,
    queryFn: async () =>
      apiClient.request({
        endpoint: `/prep-assessment-details/${userAssessmentId}/`,
      }),
  });
};

export const removePracticeExamAPIs = () => {
  return queryClient.removeQueries({ queryKey: ['RESET_PRACTICE_EXAM'] });
};

export const useCreateAttemptMutation = () => {
  return useMutation<{ assessment_id: number; status: string }, ApiError, PracticeExamUpdateSchema>({
    mutationFn: (payload) =>
      apiClient.post({
        endpoint: '/prep-assessment/',
        payload,
      }),
  });
};
